import { NodeTypes } from "src/enums/NodeTypes";
import { parseServiceErrorMessage } from "src/helpers/parseServiceError";
import { workflowStepIterator } from "src/services/workflowStepIterator";

export const PAYMENT_ACTIONS = Object.freeze({
  INITIALIZE: 0,
});

export const PAYMENT_STEPS = Object.freeze([NodeTypes.PAYMENT_VERIFICATION]);

export const PAYMENT_SUCCESS_STATUSES = new Set([
  "succeeded",
  "paid",
  "complete",
  "completed",
  "success",
  "done",
]);

export const PAYMENT_FAILED_STATUSES = new Set([
  "failed",
  "canceled",
  "cancelled",
  "requires_payment_method",
]);

export const STRIPE_CONFIRMED_STATUSES = new Set(["succeeded", "processing"]);

const SUCCESS_HTTP_STATUSES = new Set([200, 201, 202]);

const POLL_TIMEOUT_MS = 60_000;
const INITIAL_POLL_DELAY_MS = 800;
const MAX_POLL_DELAY_MS = 4_000;

const wait = (delay) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, delay);
  });

const getServiceResult = (response) => {
  const result = response?.data?.service_results?.[0];
  if (!result || !SUCCESS_HTTP_STATUSES.has(result.status)) {
    const message = parseServiceErrorMessage(result?.message);
    throw new Error(message || "Payment service is unavailable.");
  }
  return result.data || {};
};

const readPaymentStatus = (payload = {}) =>
  String(payload.status || payload.payment_status || "").toLowerCase();

export const isPaymentStep = (step) => PAYMENT_STEPS.includes(step);

export const resolvePaymentStep = (step) =>
  isPaymentStep(step) ? step : NodeTypes.PAYMENT_VERIFICATION;

/** Final/terminal ids must never be sent as the payment action target. */
export const resolvePaymentNodeId = (nodeId) => {
  const normalized =
    nodeId === undefined || nodeId === null ? "" : String(nodeId).trim();
  if (!normalized || normalized.toLowerCase() === "final") {
    throw new Error("Payment node id is invalid.");
  }
  return normalized;
};

export const createWorkflowSnapshot = (workflowData = {}) => ({
  step: workflowData.step,
  node_id: workflowData.node_id,
  index: workflowData.index,
  historyLength: Array.isArray(workflowData.history)
    ? workflowData.history.length
    : 0,
});

export const assertStripePaymentConfirmed = (stripeStatus) => {
  const normalized = String(stripeStatus || "").toLowerCase();
  if (!STRIPE_CONFIRMED_STATUSES.has(normalized)) {
    throw new Error("Payment was not completed.");
  }
};

export const resolveWorkflowAdvance = (
  workflowData = {},
  previousSnapshot = {},
  { stripeConfirmed = false } = {}
) => {
  const step = workflowData.step;

  if (step && !isPaymentStep(step)) {
    return { advance: true, reason: "step_changed" };
  }

  if (
    typeof workflowData.index === "number" &&
    typeof previousSnapshot.index === "number" &&
    workflowData.index > previousSnapshot.index
  ) {
    return { advance: true, reason: "index_advanced" };
  }

  const historyLength = Array.isArray(workflowData.history)
    ? workflowData.history.length
    : 0;
  if (historyLength > (previousSnapshot.historyLength ?? 0)) {
    return { advance: true, reason: "history_advanced" };
  }

  const serviceResult = workflowData.service_results?.[0];
  if (!serviceResult) {
    return { advance: false, pending: true, reason: "awaiting_sync" };
  }

  if (!SUCCESS_HTTP_STATUSES.has(serviceResult.status)) {
    return {
      advance: false,
      failed: true,
      message: parseServiceErrorMessage(serviceResult.message),
      reason: "service_error",
    };
  }

  const payload = serviceResult.data ?? {};
  const paymentStatus = readPaymentStatus(payload);

  if (
    payload.completed === true ||
    payload.validated === true ||
    PAYMENT_SUCCESS_STATUSES.has(paymentStatus)
  ) {
    return { advance: true, reason: "payment_confirmed" };
  }

  if (payload.failed === true || PAYMENT_FAILED_STATUSES.has(paymentStatus)) {
    return {
      advance: false,
      failed: true,
      message: payload.message,
      reason: "payment_failed",
    };
  }

  if (
    stripeConfirmed &&
    (paymentStatus === "pending" ||
      paymentStatus === "processing" ||
      !paymentStatus)
  ) {
    return { advance: false, pending: true, reason: "awaiting_webhook" };
  }

  return { advance: false, pending: true, reason: "processing" };
};

/** @deprecated Use resolveWorkflowAdvance for post-payment sync. */
export const normalizePaymentStatus = (response) => {
  const outcome = resolveWorkflowAdvance(response?.data ?? {});
  return {
    response: response?.data,
    completed: outcome.advance,
    failed: Boolean(outcome.failed),
    status: outcome.advance ? "succeeded" : outcome.failed ? "failed" : "processing",
    message: outcome.message,
  };
};

export const normalizePaymentSession = (response, publishableKey) => {
  const data = getServiceResult(response);
  const clientSecret =
    data.client_secret || data.clientSecret || data.payment_intent_client_secret;
  const paymentIntentId =
    data.provider_ref ||
    data.payment_intent_id ||
    data.paymentIntentId ||
    data.payment_intent?.id;

  if (!publishableKey) {
    throw new Error("Stripe publishable key is not configured.");
  }
  if (!clientSecret) {
    throw new Error("Payment session information is incomplete.");
  }

  return {
    publishableKey,
    clientSecret,
    paymentIntentId,
    paymentId: data.payment_id,
    provider: data.provider,
    status: data.status,
  };
};

export const initializePayment = ({
  accessToken,
  nodeId,
  retry,
  workflowId,
  step,
}) => {
  if (workflowId === undefined || workflowId === null || workflowId === "") {
    throw new Error("Payment request context is incomplete.");
  }

  return workflowStepIterator({
    access_token: accessToken,
    node_id: resolvePaymentNodeId(nodeId),
    body: {
      workflow_id: workflowId,
    },
    action_index: PAYMENT_ACTIONS.INITIALIZE,
    constextOfStep: {
      step: resolvePaymentStep(step),
      retry,
    },
  });
};

export const refreshPaymentStatus = ({ accessToken }) =>
  workflowStepIterator({
    access_token: accessToken,
  });

export const syncWorkflowAfterPayment = async ({
  accessToken,
  workflowSnapshot,
  nodeId,
  workflowId,
  step,
  stripeStatus,
  isActive = () => true,
  onPoll,
}) => {
  assertStripePaymentConfirmed(stripeStatus);

  const deadline = Date.now() + POLL_TIMEOUT_MS;
  let delay = 0;
  let syncAttempted = false;

  while (isActive() && Date.now() < deadline) {
    if (delay > 0) {
      await wait(delay);
      if (!isActive()) {
        return null;
      }
    }

    const response = await refreshPaymentStatus({ accessToken });
    let outcome = resolveWorkflowAdvance(response.data, workflowSnapshot, {
      stripeConfirmed: true,
    });

    if (outcome.advance) {
      return response.data;
    }
    if (outcome.failed) {
      throw new Error(outcome.message || "Payment failed.");
    }

    if (!syncAttempted && workflowId) {
      syncAttempted = true;
      try {
        const syncResponse = await initializePayment({
          accessToken,
          nodeId,
          retry: true,
          workflowId,
          step,
        });
        outcome = resolveWorkflowAdvance(syncResponse.data, workflowSnapshot, {
          stripeConfirmed: true,
        });
        if (outcome.advance) {
          return syncResponse.data;
        }
      } catch {
        // Payment may already be finalized; keep polling bare workflow/next.
      }
    }

    onPoll?.();
    delay =
      delay === 0
        ? INITIAL_POLL_DELAY_MS
        : Math.min(Math.round(delay * 1.5), MAX_POLL_DELAY_MS);
  }

  throw new Error("Payment confirmation timed out.");
};
